import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { AgronodButton } from '@agronod/mui-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { TagAPI } from '@/services/tag.api';
import { handleApiError } from '@/services/api';
import type { Tag } from '@/types';

/** Swedish labels for the three dimensions. */
const DIMENSION_LABEL: Record<string, string> = {
  AREA: 'Område',
  KIND: 'Dokumenttyp',
  TOPIC: 'Ämne',
};

const DIMENSIONS = ['AREA', 'KIND', 'TOPIC'] as const;

/** Empty form for a new tag. */
const emptyForm = {
  slug: '',
  displayName: '',
  dimension: 'TOPIC' as string,
  sortOrder: 0,
  isActive: true,
};

/**
 * Admin view for the tag vocabulary that replaced document categories (AG-249).
 *
 * Only admin creates tags, and the slug is the identity: it appears verbatim
 * inside `String[]` columns on documents and requirements, with no foreign key
 * to protect it. So the slug is set once and never edited — renaming a tag
 * means changing its display name. A tag still in use cannot be deleted; it is
 * deactivated instead, which hides it from the pickers while leaving it
 * readable on the documents that already carry it.
 */
export default function TagVocabularyManager() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [includeInactive, setIncludeInactive] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags', 'admin', includeInactive],
    queryFn: () => TagAPI.getAll(includeInactive),
  });

  const invalidate = (): void => {
    // Both the admin listing and every picker read the vocabulary.
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return TagAPI.update(editing.slug, {
          displayName: form.displayName.trim(),
          dimension: form.dimension,
          sortOrder: form.sortOrder,
          isActive: form.isActive,
        });
      }
      return TagAPI.create({
        slug: form.slug.trim(),
        displayName: form.displayName.trim(),
        dimension: form.dimension,
        sortOrder: form.sortOrder,
      });
    },
    onSuccess: () => {
      enqueueSnackbar(editing ? 'Taggen uppdaterad' : 'Taggen skapad', {
        variant: 'success',
      });
      setFormOpen(false);
      invalidate();
    },
    onError: (err) => setError(handleApiError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => TagAPI.remove(slug),
    onSuccess: () => {
      enqueueSnackbar('Taggen togs bort', { variant: 'success' });
      invalidate();
    },
    // The API refuses while the slug is still carried by a document or a
    // requirement, and says how many — surface that verbatim.
    onError: (err) => enqueueSnackbar(handleApiError(err), { variant: 'error' }),
  });

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (tag: Tag): void => {
    setEditing(tag);
    setForm({
      slug: tag.slug,
      displayName: tag.displayName,
      dimension: tag.dimension,
      sortOrder: tag.sortOrder,
      isActive: tag.isActive,
    });
    setError(null);
    setFormOpen(true);
  };

  const canSave =
    form.displayName.trim().length > 0 && (editing !== null || form.slug.trim().length > 0);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
          }
          label="Visa inaktiva"
        />
        <AgronodButton variant="contained" startIcon={<Add />} onClick={openCreate}>
          Ny tagg
        </AgronodButton>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Slug</TableCell>
              <TableCell>Visningsnamn</TableCell>
              <TableCell>Dimension</TableCell>
              <TableCell align="right">Ordning</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Åtgärder</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.slug} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{tag.slug}</TableCell>
                <TableCell>{tag.displayName}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={DIMENSION_LABEL[tag.dimension] ?? tag.dimension}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">{tag.sortOrder}</TableCell>
                <TableCell>
                  {tag.isActive ? (
                    <Chip size="small" label="Aktiv" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Inaktiv" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Redigera">
                    <IconButton size="small" onClick={() => openEdit(tag)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ta bort (blockeras om taggen används)">
                    <IconButton
                      size="small"
                      onClick={() => deleteMutation.mutate(tag.slug)}
                      disabled={deleteMutation.isPending}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {tags.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    Inga taggar är upplagda ännu.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Redigera ${editing.slug}` : 'Ny tagg'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              // Immutable once created: the slug lives inside documents' and
              // requirements' tag arrays, which no foreign key protects.
              disabled={editing !== null}
              required
              fullWidth
              helperText={
                editing
                  ? 'Sluggen är identiteten och kan inte ändras. Byt visningsnamn i stället.'
                  : 'Gemener utan diakritiska tecken, ord åtskilda med bindestreck, t.ex. biologisk-mangfald.'
              }
            />

            <TextField
              label="Visningsnamn"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              required
              fullWidth
            />

            <TextField
              select
              label="Dimension"
              value={form.dimension}
              onChange={(e) => setForm({ ...form, dimension: e.target.value })}
              fullWidth
              helperText="Styr bara hur väljaren grupperar — inget beteende hänger på den."
            >
              {DIMENSIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {DIMENSION_LABEL[d]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="number"
              label="Ordning"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) || 0 })}
              fullWidth
            />

            {editing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                }
                label="Aktiv — inaktiva taggar döljs i väljarna men syns kvar på dokument som redan bär dem"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <AgronodButton variant="text" onClick={() => setFormOpen(false)}>
            Avbryt
          </AgronodButton>
          <AgronodButton
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={!canSave || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Sparar…' : editing ? 'Uppdatera' : 'Skapa'}
          </AgronodButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
